// Story 8.10 (ADO-2019), AC1 (DV-7, DV-36, DV-39): the HHT card on the depot landing
// page (the 8.10 Figma exports, "Select Depot · Two HHT orders"), Story 8.1's ChoosePage, just
// above "Shop by depot".
//
//   orders waiting   "You have N handheld order(s) ready to add", what will be added, a green
//                    "Review order" (one) or "Review orders" (two or more) link, always to
//                    /hht-orders (which goes straight to the review when one order waits,
//                    DV-34). No close: the card shows while any order waits (Q9).
//   none waiting     nothing (DV-55): AC2 is carried by the header "HHT Orders" link and
//                    the /hht-orders page.
//   not applicable   nothing (the host decides what else to show).
//
// DV-39: whether the card shows, its title and its button read only the cheap waiting count
// (`useHhtWaitingCountStore`), so a failure of the classified details never hides it. The
// second line comes from the details (`useHhtOrdersStore`): a plain sum over the waiting
// orders of their Ready lines, cases and attention lines (a product in two orders counts
// twice) and the union of the carts they add to, Ambient, Chill, Fruit & Veg, Alcohol (DV-36,
// DV-30; the Chill Fruit & Veg cart at depot 25 is named apart from Chill's). Until the
// details first arrive its space is held by an empty `aria-busy` line, so the card does not
// jump; if they fail it is left out.
import { Link } from 'react-router';
import { Icon } from '../../design-system';
import { useSessionStore } from '../../auth/useSessionStore';
import { useHhtOrdersStore } from '../../hht/useHhtOrdersStore';
import { useHhtWaitingCountStore } from '../../hht/useHhtWaitingCountStore';
import { HHT_ORDERS_ROUTE } from './hhtRoutes';
import { hhtWaitingSummary } from './hhtFormat';
import linkButton from '../Cart/LinkButton.module.css';
import styles from './HhtOrders.module.css';

export function HhtWaitingCard({ className }: { readonly className?: string | undefined } = {}) {
  const applies = useHhtWaitingCountStore((s) => s.applies);
  const waitingCount = useHhtWaitingCountStore((s) => s.waitingCount);
  const detailsLoaded = useHhtOrdersStore((s) => s.loaded);
  const detailsFailed = useHhtOrdersStore((s) => s.status === 'error');
  const waiting = useHhtOrdersStore((s) => s.waiting);
  const entries = useSessionStore((s) => s.orderingChoices?.entries);

  if (!applies || waitingCount === 0) return null;

  const single = waitingCount === 1;
  const orders = single ? '1 handheld order' : `${waitingCount} handheld orders`;
  // Phase 4 (DV-53): a Default VC depot is named as its /choose tile is.
  const summary = detailsLoaded
    ? hhtWaitingSummary(
        waiting,
        (code) =>
          entries?.find(
            (entry) =>
              entry.kind === 'DEPOT' && entry.depotCode.trim().replace(/^0+(?=\d)/, '') === code,
          )?.label ?? null,
      )
    : null;

  return (
    <section
      className={
        className === undefined ? styles.waitingCard : `${styles.waitingCard} ${className}`
      }
      aria-label="Handheld orders waiting"
    >
      <Icon name="info" size={18} className={styles.waitingIcon} />
      <div className={styles.waitingText}>
        <p className={styles.waitingTitle}>You have {orders} ready to add</p>
        {summary !== null ? (
          <p className={styles.waitingLine}>{summary}</p>
        ) : !detailsLoaded && !detailsFailed ? (
          <p className={`${styles.waitingLine} ${styles.waitingLinePending}`} aria-busy="true" />
        ) : null}
      </div>
      <Link
        to={HHT_ORDERS_ROUTE}
        className={`${linkButton.linkButton} ${linkButton.primary} ${styles.waitingAction}`}
      >
        {single ? 'Review order' : 'Review orders'}
      </Link>
    </section>
  );
}
